import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

function AnimatedBlock({
  children,
  delay = 0,
  direction = 'up',
}: {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'left' | 'right';
}) {
  const { ref, visible } = useScrollAnimation(0.1);
  const translateMap = { up: 'translateY(40px)', left: 'translateX(-40px)', right: 'translateX(40px)' };

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate(0,0)' : translateMap[direction],
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function CommunityValuesSection() {
  const { ref: titleRef, visible: titleVisible } = useScrollAnimation(0.2);

  const values = [
    {
      icon: 'ri-tools-line',
      title: '기술 기반 자립',
      description: '봉제 기술을 통해 경제적 자립을 이루고, 스스로의 삶을 설계할 수 있는 힘을 키웁니다.',
      color: 'emerald',
    },
    {
      icon: 'ri-heart-line',
      title: '존중받는 노동',
      description: '기술을 가진 사람을 "선생님"이라 부르며, 모든 노동의 가치를 존중하고 인정합니다.',
      color: 'amber',
    },
    {
      icon: 'ri-leaf-line',
      title: '지속가능한 생산',
      description: '환경을 생각하는 소재 선택과 생산 방식으로 지속가능한 패션 산업을 만들어갑니다.',
      color: 'emerald',
    },
  ];

  const stories = [
    {
      icon: 'ri-user-smile-line',
      label: '아이를 키우며 일하는 사람',
      desc: '육아와 경제활동을 동시에 할 수 있는 유연한 기술 공동체',
    },
    {
      icon: 'ri-home-heart-line',
      label: '가정을 돌보는 사람',
      desc: '가정을 지키면서도 자립할 수 있는 봉제 기술의 힘',
    },
    {
      icon: 'ri-restart-line',
      label: '다시 시작하는 사람',
      desc: '새로운 출발을 기술로 응원하는 Daygreen 공동체',
    },
  ];

  return (
    <section className="py-32 px-6 bg-stone-50 overflow-hidden">
      <div className="max-w-6xl mx-auto">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="mb-24"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.9s ease',
          }}
        >
          <span className="text-emerald-600 text-sm font-semibold tracking-widest uppercase">Chapter 02</span>
          <h2
            className="text-5xl font-bold text-stone-800 mt-3 mb-6 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            기술은 사람을
            <br />
            자립하게 합니다
          </h2>
          <div className="w-16 h-0.5 bg-emerald-500"></div>
        </div>

        {/* 선생님 강조 블록 */}
        <div className="grid md:grid-cols-2 gap-20 items-center mb-28">
          <AnimatedBlock direction="left" delay={0}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/b36016abb5d29e874483581fd30505d9.jpg"
                alt="기술 공동체"
                className="w-full h-[500px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm px-6 py-4 rounded-xl shadow">
                  <p className="text-stone-800 font-bold text-base" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                    우리는 기술을 가진 사람을
                  </p>
                  <p className="text-emerald-700 font-bold text-lg mt-1" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                    "선생님"이라고 부릅니다
                  </p>
                </div>
              </div>
            </div>
          </AnimatedBlock>

          <AnimatedBlock direction="right" delay={0.2}>
            <div className="space-y-6">
              <p className="text-xl text-stone-700 leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                우리 주변에는 삶의 무게를 지고도
                <br />
                일해야 하는 사람들이 많습니다.
              </p>
              <p className="text-stone-500 leading-relaxed text-base" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                하지만 많은 직업은 그 삶을 함께 품어주지 못합니다.
                <br />
                우리는 생각했습니다.
              </p>
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-7">
                <p className="text-emerald-800 text-lg font-semibold leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                  "기술은 사람의 삶을 지키면서도
                  <br />
                  자립할 수 있는 길이 될 수 있다."
                </p>
              </div>
              <div className="space-y-4 pt-2">
                {stories.map((s, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center flex-shrink-0">
                      <i className={`${s.icon} text-emerald-600 text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-stone-800 font-semibold text-sm" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>{s.label}</p>
                      <p className="text-stone-500 text-sm mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedBlock>
        </div>

        {/* 가치 카드 3개 */}
        <div className="grid md:grid-cols-3 gap-8 mb-24">
          {values.map((value, index) => (
            <AnimatedBlock key={index} direction="up" delay={index * 0.15}>
              <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-stone-100 cursor-default h-full">
                <div className={`w-14 h-14 bg-${value.color}-100 rounded-2xl flex items-center justify-center mb-6`}>
                  <i className={`${value.icon} text-3xl text-${value.color}-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-stone-800 mb-4" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                  {value.title}
                </h3>
                <p className="text-stone-500 leading-relaxed text-sm">{value.description}</p>
              </div>
            </AnimatedBlock>
          ))}
        </div>

        {/* 공동체 이미지 풀배너 */}
        <AnimatedBlock direction="up" delay={0}>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=group%20of%20Korean%20artisans%20working%20together%20in%20bright%20sewing%20workshop%20collaborative%20craftsmanship%20multiple%20Korean%20people%20at%20sewing%20machines%20natural%20light%20warm%20atmosphere%20sustainable%20fashion%20production%20community%20teamwork%20authentic%20Korean%20workspace%20professional%20photography&width=1400&height=600&seq=daygreen-community-korean-v1&orientation=landscape"
              alt="공동체"
              className="w-full h-[420px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
              <p className="text-3xl font-bold leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                "함께 일하고, 함께 성장하며,
                <br />
                서로를 존중하는 공동체"
              </p>
            </div>
          </div>
        </AnimatedBlock>
      </div>
    </section>
  );
}
